// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * `cycle-health` — the surviving eval-rollup summation
 * (issue `cycle-head-strip`, INV-10). The engine's verdict state machine,
 * meta/eval-rate combiner and top-errors list were written for a merged
 * panel that never shipped and are now deleted; the strip's one execution
 * error-rate computation lives in `_executionTier`, which reuses
 * `selectEvalRollup` below as the eval counters' snapshot home. Keeping only
 * this selector leaves exactly one error-rate implementation in the Frontend.
 *
 * Pure: already-fetched snapshot payload in, a summed rollup out — no fetch
 * (INV-5).
 */

/** The subset of the snapshot's aggregated eval counters the tier needs. */
export interface EvalRollup {
  nEvaluations: number
  nEvaluationErrors: number
}

/** The snapshot's raw per-agent rollup, as read straight off the wire. */
export interface SnapshotEvalPayload {
  per_agent_rollup?: ReadonlyArray<{
    n_evaluations?: number
    n_evaluation_errors?: number
  }>
}

/**
 * Sum the snapshot's raw `per_agent_rollup` into the `EvalRollup` the
 * execution tier reads. `null` when the snapshot carries no per-agent rollup
 * yet — the tier turns that into an absent eval metric (INV-3), never a 0.
 */
export const selectEvalRollup = (
  payload: SnapshotEvalPayload | null | undefined,
): EvalRollup | null => {
  const rollup = payload?.per_agent_rollup
  if (rollup == null || rollup.length === 0) return null
  return {
    nEvaluations: rollup.reduce((sum, r) => sum + (r.n_evaluations ?? 0), 0),
    nEvaluationErrors: rollup.reduce(
      (sum, r) => sum + (r.n_evaluation_errors ?? 0),
      0,
    ),
  }
}
